// Texto 27 (B5): Escritório & Clientes › Equipe: mural de avisos (com "visto por"), tarefas da equipe ("Eu faço",
// "Comecei", "Terminei", filtro Minhas tarefas) e o cargo e o papel de cada membro (Admin · Edita · Só vê).
// Tudo fica no servidor (migracao-0.96-escritorio.sql); quem só vê não inclui nem pega tarefa (na tela e no servidor).
import { useCallback, useEffect, useState, useSyncExternalStore } from 'react';
import { ActivityIndicator, Alert, Pressable, Text, View } from 'react-native';
import { Btn, Chip, Field, Sheet, type Action } from '../ui';
import { C, alpha } from '../theme';
import { PickerModal } from '../Pickers';
import { CartaoApoio } from '../Recolhivel';
import { ScrollArrastavel } from '../Arrastar';
import { useDoc } from '../../store/store';
import { brYmd, parseYmd, today, ymd } from '../../lib/date';
import { t } from '../../lib/i18n';
import type { Membro, MeuGrupo } from '../../lib/grupos';
import {
  apagarAviso, apagarTarefa, avisoVisto, definirMembro, euFaco, infoMembros, muralDe, mudarSituacao, postarAviso, salvarTarefa, tarefasDe,
  type Aviso, type MembroInfo, type Papel, type SitTarefa, type Tarefa,
} from '../../lib/escritorio';

const falha = (e: any) => Alert.alert(t('Não foi possível'), String(e?.message ?? e));

// ─── As tarefas ficam guardadas aqui, para o painel ("Minhas tarefas") e a parte Equipe usarem a mesma lista ───
const cache = new Map<string, Tarefa[]>();
const ouvintes = new Set<() => void>();
let versao = 0;
export function recarregarTarefas(grupo: string) {
  return tarefasDe(grupo).then(l => { cache.set(grupo, l); versao++; ouvintes.forEach(f => f()); }, () => {});
}
export function useTarefasEsc(grupo?: string) {
  useSyncExternalStore(cb => { ouvintes.add(cb); return () => ouvintes.delete(cb); }, () => versao);
  useEffect(() => { if (grupo) recarregarTarefas(grupo); }, [grupo]);
  return grupo ? cache.get(grupo) ?? null : null;
}
export const minhasAbertas = (l: Tarefa[] | null, eu: string) => (l ?? []).filter(x => x.responsavel === eu && x.situacao !== 'feito');

/** O papel desta pessoa no escritório: quem administra edita sempre. */
export function useInfoMembros(grupo: string) {
  const [info, setInfo] = useState<MembroInfo[]>([]);
  const carregar = useCallback(() => { infoMembros(grupo).then(setInfo); }, [grupo]);
  useEffect(() => { carregar(); }, [carregar]);
  return { info, carregar };
}
export const papelDe = (info: MembroInfo[], user: string): Papel => info.find(i => i.user_id === user)?.papel ?? 'edita';
export const cargoDe = (info: MembroInfo[], user: string) => info.find(i => i.user_id === user)?.cargo ?? '';

const SO_VE = () => t('Você só pode ver este escritório: quem administra escolheu o papel "Só vê" para você. Peça a quem administra para mudar, se precisar.');

// ─── Mural ───────────────────────────────────────────────────────────────────────────────────
export function Mural({ g, cor, eu, total, podeEditar, novo, setNovo }: { g: MeuGrupo; cor: string; eu: string; total: number; podeEditar: boolean;
  novo: boolean; setNovo: (v: boolean) => void }) {
  const [lista, setLista] = useState<Aviso[] | null>(null);
  const [texto, setTexto] = useState('');
  const [verQuem, setVerQuem] = useState<Aviso | null>(null);
  const carregar = useCallback(() => {
    muralDe(g.id).then(l => {
      setLista(l);
      // abrir a Equipe já conta como "visto"
      l.filter(a => !a.vistos.some(v => v.user_id === eu)).forEach(a => avisoVisto(a.id));
    }, () => setLista([]));
  }, [g.id, eu]);
  useEffect(() => { if (eu) carregar(); }, [carregar, eu]);
  const postar = () => { if (!texto.trim()) return; postarAviso(g.id, texto.trim()).then(() => { setTexto(''); setNovo(false); carregar(); }, falha); };
  const opcoes = (a: Aviso): Action[] => [
    { label: '👀 ' + t('Quem viu'), onPress: () => setVerQuem(a) },
    ...(g.admin || a.autor === eu ? [{ label: '🗑 ' + t('Apagar aviso'), destructive: true, onPress: () => apagarAviso(a.id).then(carregar, falha) }] : []),
  ];
  const vistos = (a: Aviso) => Math.max(a.vistos.length, a.vistos.some(v => v.user_id === eu) ? a.vistos.length : a.vistos.length + 1);
  return (
    <CartaoApoio titulo={'📌 ' + t('Mural de avisos')} chave="esc:mural" inicial resumo={lista ? String(lista.length) : ''} cor={cor} gap={8}>
      {podeEditar && !novo && <Btn label={'＋ ' + t('Novo aviso')} cor={cor} outline onPress={() => setNovo(true)} />}
      {novo && (
        <View style={{ gap: 8 }}>
          <Field value={texto} onChangeText={setTexto} placeholder={t('Ex.: Feriado na sexta: escritório fechado')} multiline maxLength={1000} autoFocus />
          <View style={{ flexDirection: 'row', gap: 8 }}>
            <Btn label={t('Cancelar')} cor={cor} outline onPress={() => { setNovo(false); setTexto(''); }} style={{ flex: 1 }} />
            <Btn label={t('Fixar no mural')} cor={cor} disabled={!texto.trim()} onPress={postar} style={{ flex: 1 }} />
          </View>
        </View>
      )}
      {!lista && <ActivityIndicator color={cor} />}
      {lista && !lista.length && <Text style={{ color: C.sub, fontSize: 13 }}>{t('Nenhum aviso. Os avisos ficam fixados aqui para toda a equipe.')}</Text>}
      {(lista ?? []).map(a => (
        <Pressable key={a.id} onPress={() => setVerQuem(a)}
          style={{ backgroundColor: alpha(cor, 0.1), borderRadius: 12, padding: 10, gap: 4, borderLeftWidth: 3, borderLeftColor: cor }}>
          <Text style={{ color: C.text }}>{a.texto}</Text>
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
            <Text style={{ color: C.sub, fontSize: 11, flex: 1 }} numberOfLines={1}>{a.autor_nome} · {brYmd(ymd(new Date(a.criado_em))).slice(0, 5)}</Text>
            <Text style={{ color: C.sub, fontSize: 11, fontWeight: '700' }}>👀 {t('visto por {n} de {m}', { n: Math.min(vistos(a), total), m: total })}</Text>
          </View>
        </Pressable>
      ))}
      <Sheet visible={!!verQuem} onClose={() => setVerQuem(null)} title={t('Quem viu')}>
        <View style={{ padding: 16, gap: 8 }}>
          {!!verQuem && <Text style={{ color: C.text }}>{verQuem.texto}</Text>}
          {(verQuem?.vistos ?? []).map(v => <Text key={v.user_id} style={{ color: C.sub }}>✓ {v.nome || t('Sem nome')}</Text>)}
          {!!verQuem && opcoes(verQuem).filter(o => o.destructive).map(o => (
            <Btn key={o.label} label={o.label} cor={C.danger} outline onPress={() => { setVerQuem(null); o.onPress(); }} />))}
        </View>
      </Sheet>
    </CartaoApoio>
  );
}

// ─── Tarefas da equipe ───────────────────────────────────────────────────────────────────────
const SIT: Record<SitTarefa, { n: () => string; i: string }> = {
  afazer: { n: () => t('A fazer'), i: '⬜' }, fazendo: { n: () => t('Fazendo'), i: '⏳' }, feito: { n: () => t('Feito'), i: '✅' },
};
export function TarefasEquipe({ g, cor, eu, podeEditar, soMinhas, setSoMinhas, nova, setNova }: { g: MeuGrupo; cor: string; eu: string; podeEditar: boolean;
  soMinhas: boolean; setSoMinhas: (v: boolean) => void; nova: boolean; setNova: (v: boolean) => void }) {
  const lista = useTarefasEsc(g.id);
  const [editar, setEditar] = useState<Tarefa | null>(null);
  const [verFeitas, setVerFeitas] = useState(false);
  const hoje = today();
  const recarregar = () => recarregarTarefas(g.id);
  const fazer = (p: Promise<any>) => p.then(recarregar, falha);
  const todas = lista ?? [];
  const base = soMinhas ? todas.filter(x => x.responsavel === eu) : todas;
  const abertas = base.filter(x => x.situacao !== 'feito').sort((a, b) => (a.prazo ?? '9999').localeCompare(b.prazo ?? '9999'));
  const feitas = base.filter(x => x.situacao === 'feito');
  const minhas = minhasAbertas(lista, eu).length;
  const Cartao = ({ x }: { x: Tarefa }) => {
    const minha = x.responsavel === eu;
    const atrasada = !!x.prazo && x.prazo < hoje && x.situacao !== 'feito';
    return (
      <Pressable onPress={() => (podeEditar && (g.admin || x.criada_por === eu || minha) ? setEditar(x) : undefined)}
        style={{ backgroundColor: C.card2, borderRadius: 12, padding: 10, gap: 6, opacity: x.situacao === 'feito' ? 0.7 : 1 }}>
        <View style={{ flexDirection: 'row', gap: 8, alignItems: 'flex-start' }}>
          <Text style={{ fontSize: 16 }}>{SIT[x.situacao].i}</Text>
          <View style={{ flex: 1, gap: 2 }}>
            <Text style={{ color: C.text, fontWeight: '700', textDecorationLine: x.situacao === 'feito' ? 'line-through' : 'none' }}>{x.titulo}</Text>
            <Text style={{ color: C.sub, fontSize: 12 }}>
              {[SIT[x.situacao].n(), x.cliente ? '🗂️ ' + x.cliente : '', x.responsavel ? '👤 ' + (minha ? t('você') : x.responsavel_nome) : t('sem responsável')].filter(Boolean).join(' · ')}
            </Text>
            {!!x.prazo && <Text style={{ color: atrasada ? C.danger : C.sub, fontSize: 12, fontWeight: atrasada ? '700' : '400' }}>
              📅 {atrasada ? t('atrasada desde {d}', { d: brYmd(x.prazo).slice(0, 5) }) : t('prazo {d}', { d: brYmd(x.prazo).slice(0, 5) })}</Text>}
          </View>
        </View>
        {podeEditar && x.situacao !== 'feito' && (
          <View style={{ flexDirection: 'row', gap: 8, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
            {!minha && (!x.responsavel || g.admin) && <Chip small cor={cor} on={false} label={'🙋 ' + t('Eu faço')} onPress={() => fazer(euFaco(x))} />}
            {(minha || g.admin) && x.situacao === 'afazer' && !!x.responsavel && <Chip small cor={cor} on={false} label={'▶️ ' + t('Comecei')} onPress={() => fazer(mudarSituacao(x, 'fazendo'))} />}
            {(minha || g.admin) && !!x.responsavel && <Chip small cor={cor} on label={'✅ ' + t('Terminei')} onPress={() => fazer(mudarSituacao(x, 'feito'))} />}
          </View>
        )}
        {podeEditar && x.situacao === 'feito' && (minha || g.admin) && (
          <Pressable onPress={() => fazer(mudarSituacao(x, 'afazer'))} style={{ alignSelf: 'flex-end' }}><Text style={{ color: C.sub, fontSize: 12 }}>↩ {t('Reabrir')}</Text></Pressable>
        )}
      </Pressable>
    );
  };
  return (
    <CartaoApoio titulo={'✅ ' + t('Tarefas da equipe')} chave="esc:tarefas" inicial cor={cor} gap={8}
      resumo={lista ? ((n: number) => (n === 1 ? t('1 aberta') : t('{n} abertas', { n })))(todas.filter(x => x.situacao !== 'feito').length) : ''}>
      <View style={{ flexDirection: 'row', gap: 6, flexWrap: 'wrap' }}>
        <Chip small cor={cor} on={!soMinhas} label={t('Todas')} onPress={() => setSoMinhas(false)} />
        <Chip small cor={cor} on={soMinhas} label={t('Minhas tarefas ({n})', { n: minhas })} onPress={() => setSoMinhas(true)} />
      </View>
      {podeEditar && <Btn label={'＋ ' + t('Nova tarefa')} cor={cor} outline onPress={() => setNova(true)} />}
      {!lista && <ActivityIndicator color={cor} />}
      {lista && !abertas.length && <Text style={{ color: C.sub, fontSize: 13 }}>{soMinhas ? t('Nenhuma tarefa sua aberta. Toque em "Eu faço" numa tarefa da equipe.') : t('Nenhuma tarefa aberta.')}</Text>}
      {abertas.map(x => <Cartao key={x.id} x={x} />)}
      {!!feitas.length && <Pressable onPress={() => setVerFeitas(!verFeitas)}><Text style={{ color: C.sub, fontSize: 13 }}>{verFeitas ? '▾' : '▸'} {t('Feitas ({n})', { n: feitas.length })}</Text></Pressable>}
      {verFeitas && feitas.map(x => <Cartao key={x.id} x={x} />)}
      <TarefaSheet visible={nova || !!editar} g={g} cor={cor} tarefa={editar} podeApagar={!!editar && (g.admin || editar.criada_por === eu)}
        onClose={() => { setNova(false); setEditar(null); }} onPronto={recarregar} />
    </CartaoApoio>
  );
}

/** Nova tarefa (ou editar): título, cliente ligado (opcional, de Meus clientes), prazo; já sai com responsável se quiser. */
export function TarefaSheet({ visible, g, cor, tarefa, podeApagar, onClose, onPronto, reuniao, membros }: {
  visible: boolean; g: MeuGrupo; cor: string; tarefa?: Tarefa | null; podeApagar?: boolean; onClose: () => void; onPronto: () => void;
  reuniao?: string | null; membros?: Membro[] }) {
  const clientes = (useDoc('clientes')?.regs ?? []).filter(r => r.col === 'cliente');
  const [titulo, setTitulo] = useState('');
  const [cliente, setCliente] = useState('');
  const [prazo, setPrazo] = useState<string | null>(null);
  const [resp, setResp] = useState<string | null>(null);
  const [picker, setPicker] = useState(false);
  const [salvando, setSalvando] = useState(false);
  useEffect(() => { if (visible) { setTitulo(tarefa?.titulo ?? ''); setCliente(tarefa?.cliente ?? ''); setPrazo(tarefa?.prazo ?? null); setResp(null); } }, [visible, tarefa]);
  const q = cliente.trim().toLowerCase();
  const sugestoes = q ? clientes.filter(c => String(c.c.nome ?? '').toLowerCase().includes(q) && String(c.c.nome) !== cliente).slice(0, 4) : [];
  const salvar = async () => {
    if (!titulo.trim()) return;
    setSalvando(true);
    try { await salvarTarefa(g.id, { id: tarefa?.id, titulo: titulo.trim(), cliente: cliente.trim(), prazo, responsavel: resp, reuniao: reuniao ?? null }); onClose(); onPronto(); }
    catch (e) { falha(e); } finally { setSalvando(false); }
  };
  const apagar = () => tarefa && Alert.alert(t('Apagar a tarefa?'), tarefa.titulo, [{ text: t('Cancelar'), style: 'cancel' },
    { text: t('Apagar'), style: 'destructive', onPress: () => apagarTarefa(tarefa).then(() => { onClose(); onPronto(); }, falha) }]);
  return (
    <Sheet visible={visible} onClose={onClose} title={tarefa ? t('Editar tarefa') : reuniao ? t('Tarefa da reunião') : t('Nova tarefa')}>
      <ScrollArrastavel keyboardShouldPersistTaps="handled" contentContainerStyle={{ padding: 16, gap: 12, paddingBottom: 60 }}>
        <Field value={titulo} onChangeText={setTitulo} placeholder={t('O que precisa ser feito? (ex.: Apurar o DAS de março)')} maxLength={200} autoFocus />
        <Field value={cliente} onChangeText={setCliente} placeholder={'🗂️ ' + t('Cliente (opcional)')} maxLength={120} />
        {!!sugestoes.length && (
          <View style={{ flexDirection: 'row', gap: 6, flexWrap: 'wrap' }}>
            {sugestoes.map(c => <Chip key={c.id} small cor={cor} on={false} label={String(c.c.nome)} onPress={() => setCliente(String(c.c.nome))} />)}
          </View>
        )}
        <Pressable onPress={() => setPicker(true)} style={{ backgroundColor: C.card2, borderRadius: 12, padding: 14, flexDirection: 'row', alignItems: 'center' }}>
          <Text style={{ color: C.text, flex: 1 }}>📅 {prazo ? t('Prazo: {d}', { d: brYmd(prazo) }) : t('Prazo (opcional)')}</Text>
          {!!prazo && <Pressable onPress={() => setPrazo(null)} hitSlop={8}><Text style={{ color: C.sub }}>✕</Text></Pressable>}
        </Pressable>
        {!tarefa && !!membros?.length && (<>
          <Text style={{ color: C.sub, fontSize: 12 }}>{t('Responsável (opcional; senão, alguém toca em "Eu faço")')}</Text>
          <View style={{ flexDirection: 'row', gap: 6, flexWrap: 'wrap' }}>
            {membros.map(m => <Chip key={m.user_id} small cor={cor} on={resp === m.user_id} label={m.nome || t('Sem nome')} onPress={() => setResp(resp === m.user_id ? null : m.user_id)} />)}
          </View>
        </>)}
        {!titulo.trim() && <Text style={{ color: C.warn, fontSize: 12, fontWeight: '700' }}>✍️ {t('Escreva a tarefa para poder salvar.')}</Text>}
        <Btn label={salvando ? '...' : t('Salvar')} cor={cor} disabled={salvando || !titulo.trim()} onPress={salvar} />
        {podeApagar && <Btn label={'🗑 ' + t('Apagar')} cor={C.danger} outline onPress={apagar} />}
      </ScrollArrastavel>
      {picker && <PickerModal mode="date" cor={cor} value={parseYmd(prazo ?? today())} onClose={() => setPicker(false)}
        onPick={d => { setPicker(false); setPrazo(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`); }} />}
    </Sheet>
  );
}

// ─── Cargo e papel de um membro ──────────────────────────────────────────────────────────────
export function MembroSheet({ g, m, info, cor, onClose, onPronto }: { g: MeuGrupo; m: Membro | null; info: MembroInfo[]; cor: string; onClose: () => void; onPronto: () => void }) {
  const [cargo, setCargo] = useState('');
  const [papel, setPapel] = useState<Papel>('edita');
  useEffect(() => { if (m) { setCargo(cargoDe(info, m.user_id)); setPapel(papelDe(info, m.user_id)); } }, [m, info]);
  const salvar = () => { if (!m) return; definirMembro(g.id, m.user_id, cargo.trim(), papel).then(() => { onClose(); onPronto(); }, falha); };
  return (
    <Sheet visible={!!m} onClose={onClose} title={m?.nome || t('Membro')}>
      <View style={{ padding: 16, gap: 12 }}>
        <Text style={{ color: C.sub, fontSize: 12 }}>{t('Cargo (livre)')}</Text>
        <Field value={cargo} onChangeText={setCargo} placeholder={t('Ex.: Advogada, Assistente, Estagiário')} maxLength={60} />
        <Text style={{ color: C.sub, fontSize: 12 }}>{t('Papel')}</Text>
        {m?.admin ? <Text style={{ color: C.text }}>⭐ {t('Admin: pode tudo no escritório.')}</Text> : (
          <View style={{ gap: 6 }}>
            <Chip cor={cor} on={papel === 'edita'} label={'✏️ ' + t('Edita: inclui tarefas, avisos, documentos e reuniões')} onPress={() => setPapel('edita')} />
            <Chip cor={cor} on={papel === 've'} label={'👁 ' + t('Só vê: acompanha tudo, sem incluir nem mudar')} onPress={() => setPapel('ve')} />
          </View>
        )}
        <Btn label={t('Salvar')} cor={cor} onPress={salvar} />
      </View>
    </Sheet>
  );
}

export { SO_VE };
